import React, { useState, useEffect } from 'react';
import Modal from '../Modal/Modal';
import './ChangePasswordModal.css';
import { useAuthContext } from '../../context/AuthContext';

const ChangePasswordModal = ({ isOpen, onClose, onSubmit, loading, title = "Cambio de Contraseña Requerido",
    subtitle = "Por seguridad, debes cambiar tu contraseña temporal" }) => {
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [errors, setErrors] = useState({});
    const [isFormValid, setIsFormValid] = useState(false);
    const { currentUser } = useAuthContext();

    const checkPasswordRequirements = (password) => {
        return [
            { id: 'length', label: 'Mínimo 8 caracteres', met: password.length >= 8 },
            { id: 'uppercase', label: 'Al menos una mayúscula', met: /[A-Z]/.test(password) },
            { id: 'lowercase', label: 'Al menos una minúscula', met: /[a-z]/.test(password) },
            { id: 'number', label: 'Al menos un número', met: /\d/.test(password) },
            { id: 'special', label: 'Al menos un carácter especial', met: /[!@#$%^&*(),.?":{}|<>]/.test(password) },
            { id: 'same-password-fields', label: 'Las contraseñas coinciden', met: password === confirmPassword }
        ];
    };

    const validateForm = () => {
        const newErrors = {};

        if (!newPassword) {
            newErrors.newPassword = 'La nueva contraseña es requerida';
        }

        if (!confirmPassword) {
            newErrors.confirmPassword = 'Confirmar contraseña es requerido';
        }

        if (newPassword && confirmPassword && newPassword !== confirmPassword) {
            newErrors.confirmPassword = 'Las contraseñas no coinciden';
        }

        const requirements = checkPasswordRequirements(newPassword);
        const allRequirementsMet = requirements.every(req => req.met);

        if (newPassword && !allRequirementsMet) {
            newErrors.requirements = 'No se cumplen todos los requisitos de contraseña';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Validar formulario en tiempo real
    useEffect(() => {
        const requirements = checkPasswordRequirements(newPassword);
        const allRequirementsMet = requirements.every(req => req.met);
        const passwordsMatch = newPassword === confirmPassword;
        const bothPasswordsFilled = newPassword && confirmPassword;

        const isValid = allRequirementsMet && passwordsMatch && bothPasswordsFilled;
        setIsFormValid(isValid);
    }, [newPassword, confirmPassword]);

    const handleSubmit = (e) => {
        e.preventDefault();

        if (validateForm()) {
            const { id } = currentUser;
            onSubmit({ newPassword, cognito_id: id });
        }
    };

    const requirements = checkPasswordRequirements(newPassword);

    if (!isOpen) return null;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="medium"
            showCloseButton={true}
            closeOnBackdropClick={true}
        >
            <div className="change-password-content">
                <div className="modal-header">
                    <h2>{title}</h2>
                    <p className="modal-subtitle">
                        {subtitle}
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="change-password-form">
                    <div className="form-group">
                        <label htmlFor="newPassword">Nueva Contraseña</label>
                        <div className="password-input-container">
                            <input
                                type={showPassword ? 'text' : 'password'}
                                id="newPassword"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                className={errors.newPassword ? 'error' : ''}
                                placeholder="Ingresa tu nueva contraseña"
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? '👁️' : '👁️‍🗨️'}
                            </button>
                        </div>
                        {errors.newPassword && (
                            <span className="error-message">{errors.newPassword}</span>
                        )}
                    </div>

                    <div className="form-group">
                        <label htmlFor="confirmPassword">Confirmar Contraseña</label>
                        <div className="password-input-container">
                            <input
                                type={showConfirmPassword ? 'text' : 'password'}
                                id="confirmPassword"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className={errors.confirmPassword ? 'error' : ''}
                                placeholder="Confirma tu nueva contraseña"
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            >
                                {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                            </button>
                        </div>
                        {errors.confirmPassword && (
                            <span className="error-message">{errors.confirmPassword}</span>
                        )}
                    </div>

                    <div className="requirements-section">
                        <h3>Requisitos de Contraseña</h3>
                        <div className="requirements-list">
                            {requirements.map((req) => (
                                <div
                                    key={req.id}
                                    className={`requirement-item ${req.met ? 'met' : 'not-met'}`}
                                >
                                    <span className="requirement-icon">
                                        {req.met ? '✅' : '❌'}
                                    </span>
                                    <span className="requirement-label">{req.label}</span>
                                </div>
                            ))}
                        </div>
                        {errors.requirements && (
                            <span className="error-message">{errors.requirements}</span>
                        )}
                    </div>

                    <div className="modal-actions">
                        <button
                            type="submit"
                            className={`submit-button ${!isFormValid ? 'disabled' : ''}`}
                            disabled={loading || !isFormValid}
                        >
                            {loading ? 'Cambiando contraseña...' : 'Cambiar Contraseña'}
                        </button>
                    </div>
                </form>
            </div>
        </Modal>
    );
};

export default ChangePasswordModal;